import { useRouterState } from '@tanstack/react-router';
import React from 'react';

import { useDarkMode } from '@/app/hooks/use-dark-mode';
import { Navbar, NavbarDivider, NavbarItem, NavbarSection, NavbarSpacer } from '@/common/components/navbar';
import { Sidebar, SidebarBody, SidebarFooter, SidebarHeader, SidebarItem, SidebarLabel, SidebarSection, SidebarSpacer } from '@/common/components/sidebar';
import { StackedLayout } from '@/common/components/stacked-layout';
import { LinkIcon, MagnifyingGlassIcon, RssIcon, UsersIcon } from '@/icons/fa/outline';
import { MoonIcon, SunBrightIcon } from '@/icons/fa/regular';
import { NavbarUserMenu, SidebarUserMenu } from '@/shared/components/user-menu';

interface NavigationLink {
  label: React.ReactNode;
  href: '/' | '/links' | '/feeds' | '/users';
  icon?: React.ReactNode;
}

const navigationLinks: NavigationLink[] = [
  {
    label: 'Links',
    href: '/links',
    icon: <LinkIcon />
  },
  {
    label: 'Feeds',
    href: '/feeds',
    icon: <RssIcon />
  },
  {
    label: 'Users',
    href: '/users',
    icon: <UsersIcon />
  }
];

/**
 * A layout component for the dashboard that includes a navigation sidebar, top navbar, and
 * handles the rendering of children components within the layout structure.
 */
export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const location = useRouterState({ select: state => state.location });

  const isCurrent = (path: string) => location.pathname === path;

  const { isDarkMode, toggle } = useDarkMode();

  return (
    <StackedLayout
      navbar={(
        <Navbar>
          <NavbarSection className="max-lg:hidden">
            <div className="flex items-center gap-3">
              <span className="text-xl font-semibold text-orange-600 dark:text-orange-500">Red Lynx</span>
            </div>
          </NavbarSection>
          <NavbarDivider className="max-lg:hidden" />
          <NavbarSection className="max-lg:hidden">
            {navigationLinks.map(link => (
              <NavbarItem
                key={link.href}
                current={isCurrent(link.href)}
                href={link.href}
              >
                {link.label}
              </NavbarItem>
            ))}
          </NavbarSection>
          <NavbarSpacer />
          <NavbarSection>
            <NavbarItem onClick={() => {}}>
              <MagnifyingGlassIcon />
            </NavbarItem>
            <NavbarItem onClick={toggle}>
              {isDarkMode ? <SunBrightIcon /> : <MoonIcon />}
            </NavbarItem>
            <NavbarUserMenu />
          </NavbarSection>
        </Navbar>
      )}
      sidebar={(
        <Sidebar>
          <SidebarHeader>
            <SidebarItem className="lg:mb-2.5">
              <SidebarLabel className="text-lg font-semibold text-orange-600 dark:text-orange-500">Red Lynx</SidebarLabel>
            </SidebarItem>
          </SidebarHeader>
          <SidebarBody>
            <SidebarSection>
              {navigationLinks.map(link => (
                <SidebarItem
                  key={link.href}
                  current={isCurrent(link.href)}
                  href={link.href}
                >
                  {link.icon}
                  <SidebarLabel>{link.label}</SidebarLabel>
                </SidebarItem>
              ))}
            </SidebarSection>
            <SidebarSpacer />
            <SidebarSection>
              <SidebarItem onClick={toggle}>
                {isDarkMode ? <SunBrightIcon /> : <MoonIcon />}
                <SidebarLabel>{isDarkMode ? 'Light' : 'Dark'}</SidebarLabel>
              </SidebarItem>
            </SidebarSection>
          </SidebarBody>
          <SidebarFooter className="max-lg:hidden">
            <SidebarUserMenu />
          </SidebarFooter>
        </Sidebar>
      )}
    >
      {children}
    </StackedLayout>
  );
}
